import React, { useState } from "react";
import { PageContainer, PageHeader, Card } from "./Shared";
import { adminApi } from "./api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { runValidators, validators, sanitizers } from "./validators";

const FIELDS = [
  { key: "contact_name", label: "Primary contact name (shown in Connect dialog)",
    validate: [validators.maxLen(80)], placeholder: "Mr. Sajid Husain", maxLength: 80 },
  { key: "whatsapp_number", label: "WhatsApp number (digits only, e.g. 971501234567)",
    sanitizer: sanitizers.digitsOnly,
    validate: [validators.whatsapp],
    placeholder: "971501234567", maxLength: 15 },
  { key: "phone", label: "Primary phone (display, e.g. +971 50 123 4567)",
    sanitizer: sanitizers.phoneAllowed,
    validate: [validators.phone], maxLength: 22 },
  { key: "phones_extra", label: "Additional phones (comma-separated · e.g. +971 5x xxx xxxx, +971 5y yyy yyyy)",
    validate: [validators.maxLen(400)], maxLength: 400 },
  { key: "email", label: "Primary email",
    validate: [validators.email], placeholder: "info@opsysdigital.com", maxLength: 80 },
  { key: "email_alt", label: "Secondary email",
    validate: [validators.email, validators.maxLen(80)], placeholder: "sales@opsysdigital.com", maxLength: 80 },
  { key: "emails_extra", label: "Additional emails (comma-separated)",
    validate: [validators.maxLen(400)], maxLength: 400 },
  { key: "address", label: "Address",
    validate: [validators.maxLen(200)], maxLength: 200 },
  { key: "map_embed", label: "Google Maps embed URL (from Share → Embed → src attribute)",
    validate: [validators.maxLen(1000)], placeholder: "https://www.google.com/maps/embed?pb=…", maxLength: 1000 },
  { key: "map_url", label: "Google Maps direct link",
    validate: [validators.url, validators.maxLen(500)], placeholder: "https://maps.google.com/…", maxLength: 500 },
  { key: "office_hours", label: "Office hours",
    validate: [validators.maxLen(80)], maxLength: 80 },
  { key: "linkedin_url", label: "LinkedIn URL",
    validate: [validators.url], placeholder: "https://www.linkedin.com/company/…", maxLength: 200 },
  { key: "instagram_url", label: "Instagram URL",
    validate: [validators.url], placeholder: "https://www.instagram.com/…", maxLength: 200 },
  { key: "facebook_url", label: "Facebook URL",
    validate: [validators.url], placeholder: "https://www.facebook.com/…", maxLength: 200 },
  { key: "twitter_url", label: "Twitter / X URL",
    validate: [validators.url], placeholder: "https://x.com/…", maxLength: 200 },
  { key: "youtube_url", label: "YouTube URL",
    validate: [validators.url], placeholder: "https://www.youtube.com/@…", maxLength: 200 },
];

export default function SettingsManager() {
  const [data, setData] = useState({});
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);

  React.useEffect(() => { adminApi.get("/admin/settings").then((r) => setData(r.data)); }, []);

  const handleChange = (f, raw) => {
    const value = f.sanitizer ? f.sanitizer(raw) : raw;
    setData((d) => ({ ...d, [f.key]: value }));
    setErrors((e) => ({ ...e, [f.key]: runValidators(value, f.validate || []) }));
  };

  const save = async () => {
    // Validate all before saving
    const newErr = {};
    for (const f of FIELDS) {
      const err = runValidators(data[f.key] || "", f.validate || []);
      if (err) newErr[f.key] = err;
    }
    setErrors(newErr);
    if (Object.values(newErr).some(Boolean)) {
      toast.error("Fix the highlighted fields");
      return;
    }
    setBusy(true);
    try {
      await adminApi.put("/admin/settings", { settings: data });
      toast.success("Saved");
    } catch { toast.error("Save failed"); }
    finally { setBusy(false); }
  };

  return (
    <PageContainer>
      <PageHeader title="Site Settings" subtitle="Public contact info, social URLs and the WhatsApp number."
        action={<Button onClick={save} disabled={busy} className="text-white" style={{ background: "#1B6FE0" }} data-testid="settings-save">{busy ? "Saving…" : "Save"}</Button>} />
      <Card className="p-6 grid md:grid-cols-2 gap-4" data-testid="settings-fields">
        {FIELDS.map((f) => (
          <div key={f.key} data-testid={`setting-${f.key}`}>
            <Label>{f.label}</Label>
            <Input
              className="mt-1.5"
              placeholder={f.placeholder || ""}
              value={data[f.key] || ""}
              maxLength={f.maxLength}
              onChange={(e) => handleChange(f, e.target.value)}
              data-testid={`setting-input-${f.key}`}
            />
            {errors[f.key] && (
              <div className="text-[12px] mt-1" style={{ color: "#1B6FE0" }} data-testid={`setting-err-${f.key}`}>
                {errors[f.key]}
              </div>
            )}
          </div>
        ))}
      </Card>
    </PageContainer>
  );
}
